/**
 * 进度跟踪模块 - 类型定义
 * Progress Tracking Module - Type Definitions
 * 
 * 注意：已移除合同(Contract)相关类型，所有数据直接与项目(Project)关联
 */

// ==================== 工程项相关类型 ====================

/**
 * 工程项状态
 */
export enum WorkItemStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  INSPECTING = 'inspecting',
  ACCEPTED = 'accepted',
  RECTIFYING = 'rectifying'
}

/**
 * 计价方式
 */
export enum PriceType {
  UNIT = 'unit',
  TOTAL = 'total'
}

/**
 * 工程项 - 直接关联项目
 */
export interface IWorkItem {
  _id: string;
  itemNo: string;
  name: string;
  projectId: string | { _id: string; name: string; projectNo?: string };
  parentItemId?: string | { _id: string; name: string; itemNo: string };
  level: number;
  sortOrder: number;
  category: string;
  unit: string;
  contractQuantity: number;
  completedQuantity: number;
  priceType: PriceType;
  unitPrice?: number;
  totalPrice?: number;
  currentUnitPrice?: number;
  plannedStartDate?: string;
  plannedEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  status: WorkItemStatus;
  progressPercentage: number;
  isSettlementPoint: boolean;
  settlementPercentage?: number;
  isSubcontracted: boolean;
  subcontractorId?: string | { _id: string; name: string };
  isAddition: boolean;
  additionId?: string;
  managerId?: string | { _id: string; username: string };
  supervisorId?: string | { _id: string; username: string };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  children?: IWorkItem[];
}

// ==================== 进度记录相关类型 ====================

/**
 * 附件类型
 */
export enum AttachmentType {
  IMAGE = 'image',
  DOCUMENT = 'document'
}

/**
 * 进度附件
 */
export interface IProgressAttachment {
  type: AttachmentType;
  url: string;
  name: string;
  thumbnailUrl?: string;
  uploadedAt: string;
}

/**
 * 进度记录 - 直接关联项目
 */
export interface IProgressRecord {
  _id: string;
  workItemId: string | { _id: string; name: string; itemNo: string; unit: string };
  projectId: string | { _id: string; name: string; projectNo?: string };
  recordDate: string;
  previousQuantity: number;
  currentQuantity: number;
  totalQuantity: number;
  progressPercentage: number;
  previousAmount: number;
  currentAmount: number;
  totalAmount: number;
  attachments?: IProgressAttachment[];
  description?: string;
  recordedBy: string | { _id: string; username: string };
  recordedByName: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== 增项相关类型 ====================

export enum AdditionReason {
  SITE_ADDITION = 'site_addition',
  CLIENT_REQUEST = 'client_request',
  DESIGN_CHANGE = 'design_change',
  CONSTRUCTION_OPTIMIZATION = 'construction_optimization',
  CONSTRUCTION_SUPPORT = 'construction_support',
  REPAIR_REWORK = 'repair_rework',
  OTHER = 'other'
}

export enum AdditionStatus {
  PENDING = 'pending',
  ASSIGNED = 'assigned',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  SETTLED = 'settled',
  ARCHIVED = 'archived',
  SUSPENDED = 'suspended',
  CANCELLED = 'cancelled'
}

export enum ExecutorType {
  SELF = 'self',
  SUBCONTRACT = 'subcontract',
  MIXED = 'mixed'
}

export enum UrgencyLevel {
  NORMAL = 'normal',
  URGENT = 'urgent',
  CRITICAL = 'critical'
}

export interface IAddition {
  _id: string;
  additionNo: string;
  batchId?: string;
  projectId: string | { _id: string; name: string; projectNo?: string };
  title: string;
  content: string;
  category: string;
  reason: AdditionReason;
  reasonDescription?: string;
  location?: string;
  urgencyLevel: UrgencyLevel;

  executorType: ExecutorType;
  executorId?: string | { _id: string; username: string; realName?: string };
  executorName?: string;
  teamName?: string;

  isSubcontracted: boolean;
  subcontractorId?: string | { _id: string; name: string; shortName?: string; contactPerson?: string; phone?: string };
  subcontractorName?: string;
  subcontractLeader?: string;
  subcontractPhone?: string;
  subcontractAssignedAt?: string;
  subcontractSettlementMethod?: AdditionSubcontractSettlementMethod;
  subcontractAmount?: number;
  subcontractRemark?: string;
  linkedSubcontractCostId?: string;
  subcontractCostSyncStatus?: 'pending' | 'synced' | 'failed';
  excludeFromCostAggregation?: boolean;

  estimatedAmount: number;
  plannedAmount?: number;
  actualAmount: number;
  settlementAmount?: number;
  laborAmount?: number;
  materialAmount?: number;
  machineryAmount?: number;
  otherAmount?: number;

  plannedStartDate?: string;
  plannedEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  affectsSchedule: boolean;

  status: AdditionStatus;
  progressPercent: number;

  completedAt?: string;
  completedByName?: string;
  isSettled: boolean;
  settledAt?: string;
  settledByName?: string;
  settlementRemark?: string;
  isArchived: boolean;

  cancelReason?: string;
  suspendReason?: string;

  attachments?: { type: string; url: string; name: string; description?: string }[];
  remark?: string;

  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export type AdditionSubcontractSettlementMethod = SubcontractSettlementMethod;

export interface IAdditionBatch {
  _id: string;
  batchNo: string;
  projectId: string | { _id: string; name: string; projectNo?: string };
  entryDate: string;
  sourceType: AdditionReason;
  sourceDescription?: string;
  location?: string;
  itemCount: number;
  totalEstimatedAmount: number;
  totalActualAmount: number;
  totalSettlementAmount: number;
  createdByName: string;
  createdAt: string;
}

export interface IAdditionItemLog {
  _id: string;
  additionId: string;
  projectId: string;
  logDate: string;
  status: AdditionStatus;
  progressPercent: number;
  completedWork?: string;
  actualAmountIncrement?: number;
  issues?: string;
  coordinationNeeded?: string;
  attachments?: { type: string; url: string; name: string }[];
  remark?: string;
  createdByName: string;
  createdAt: string;
}

// ==================== 整改相关类型 ====================

/**
 * 整改来源
 */
export enum RectificationSource {
  QUALITY_CHECK = 'quality_check',
  OWNER_ACCEPTANCE = 'owner_acceptance',
  SAFETY_CHECK = 'safety_check',
  OTHER = 'other'
}

/**
 * 整改严重程度
 */
export enum RectificationSeverity {
  MINOR = 'minor',
  MAJOR = 'major',
  CRITICAL = 'critical'
}

/**
 * 整改状态
 */
export enum RectificationStatus {
  PENDING = 'pending',
  NOTIFIED = 'notified',
  IN_PROGRESS = 'in_progress',
  SUBMITTED = 'submitted',
  DIRECTOR_ACCEPTED = 'director_accepted',
  OWNER_ACCEPTED = 'owner_accepted',
  CLOSED = 'closed'
}

/**
 * 整改过程记录
 */
export interface IProcessHistory {
  action: string;
  userId: string;
  userName: string;
  comment?: string;
  attachments?: string[];
  timestamp: string;
}

/**
 * 整改 - 直接关联项目
 */
export interface IRectification {
  _id: string;
  rectificationNo: string;
  projectId: string | { _id: string; name: string; projectNo?: string };
  workItemId: string | { _id: string; name: string; itemNo: string };
  source: RectificationSource;
  severity: RectificationSeverity;
  title: string;
  description: string;
  deductionPercentage: number;
  deductedQuantity: number;
  deductedAmount: number;
  originalProgress: number;
  responsibleUserId?: string | { _id: string; username: string };
  responsibleUserName?: string;
  responsibleParty?: string;
  penaltyAmount: number;
  penaltyDescription?: string;
  deadline?: string;
  status: RectificationStatus;
  processHistory: IProcessHistory[];
  issueAttachments?: { url: string; name: string; uploadedAt: string }[];
  rectificationAttachments?: { url: string; name: string; uploadedAt: string }[];
  directorAcceptedBy?: string;
  directorAcceptedAt?: string;
  directorComment?: string;
  ownerAcceptedBy?: string;
  ownerAcceptedAt?: string;
  ownerComment?: string;
  progressRestored: boolean;
  restoredAt?: string;
  restoredProgress: number;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== 分包商相关类型 ====================

/**
 * 分包商状态
 */
export enum SubcontractorStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  BLACKLISTED = 'blacklisted'
}

/**
 * 分包商
 */
export interface ISubcontractor {
  _id: string;
  name: string;
  shortName?: string;
  contactPerson: string;
  phone: string;
  email?: string;
  address?: string;
  businessLicense?: string;
  status: SubcontractorStatus;
  totalProjects: number;
  totalAmount: number;
  averageEvaluation: number;
  createdAt: string;
  updatedAt: string;
}

// ==================== 通用类型 ====================

/**
 * 分页参数
 */
export interface IPaginationParams {
  page?: number;
  limit?: number;
}

/**
 * 分页响应
 */
export interface IPaginationResponse<T> {
  list: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * API响应
 */
export interface IApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

// ==================== 状态标签映射 ====================

export const WORK_ITEM_STATUS_MAP: Record<WorkItemStatus, { label: string; color: string }> = {
  [WorkItemStatus.PENDING]: { label: '未开始', color: 'default' },
  [WorkItemStatus.IN_PROGRESS]: { label: '进行中', color: 'processing' },
  [WorkItemStatus.COMPLETED]: { label: '已完成', color: 'success' },
  [WorkItemStatus.INSPECTING]: { label: '验收中', color: 'cyan' },
  [WorkItemStatus.ACCEPTED]: { label: '已验收', color: 'green' },
  [WorkItemStatus.RECTIFYING]: { label: '整改中', color: 'error' }
};

export const ADDITION_STATUS_MAP: Record<AdditionStatus, { label: string; color: string }> = {
  [AdditionStatus.PENDING]: { label: '待安排', color: 'default' },
  [AdditionStatus.ASSIGNED]: { label: '已安排', color: 'processing' },
  [AdditionStatus.IN_PROGRESS]: { label: '施工中', color: 'warning' },
  [AdditionStatus.COMPLETED]: { label: '已完工', color: 'success' },
  [AdditionStatus.SETTLED]: { label: '已结算', color: 'green' },
  [AdditionStatus.ARCHIVED]: { label: '已归档', color: 'default' },
  [AdditionStatus.SUSPENDED]: { label: '已挂起', color: 'orange' },
  [AdditionStatus.CANCELLED]: { label: '已取消', color: 'default' }
};

export const RECTIFICATION_STATUS_MAP: Record<RectificationStatus, { label: string; color: string }> = {
  [RectificationStatus.PENDING]: { label: '待处理', color: 'default' },
  [RectificationStatus.NOTIFIED]: { label: '已通知', color: 'processing' },
  [RectificationStatus.IN_PROGRESS]: { label: '整改中', color: 'warning' },
  [RectificationStatus.SUBMITTED]: { label: '已提交', color: 'cyan' },
  [RectificationStatus.DIRECTOR_ACCEPTED]: { label: '总监已验收', color: 'blue' },
  [RectificationStatus.OWNER_ACCEPTED]: { label: '业主已验收', color: 'green' },
  [RectificationStatus.CLOSED]: { label: '已关闭', color: 'success' }
};

export const ADDITION_REASON_MAP: Record<AdditionReason, string> = {
  [AdditionReason.SITE_ADDITION]: '现场临时增加',
  [AdditionReason.CLIENT_REQUEST]: '甲方要求',
  [AdditionReason.DESIGN_CHANGE]: '设计变更',
  [AdditionReason.CONSTRUCTION_OPTIMIZATION]: '施工优化',
  [AdditionReason.CONSTRUCTION_SUPPORT]: '施工配合',
  [AdditionReason.REPAIR_REWORK]: '返工修复',
  [AdditionReason.OTHER]: '其他'
};

export const EXECUTOR_TYPE_MAP: Record<ExecutorType, string> = {
  [ExecutorType.SELF]: '自有施工',
  [ExecutorType.SUBCONTRACT]: '分包施工',
  [ExecutorType.MIXED]: '混合施工'
};

export const URGENCY_LEVEL_MAP: Record<UrgencyLevel, { label: string; color: string }> = {
  [UrgencyLevel.NORMAL]: { label: '普通', color: 'default' },
  [UrgencyLevel.URGENT]: { label: '紧急', color: 'orange' },
  [UrgencyLevel.CRITICAL]: { label: '特急', color: 'red' }
};

export const RECTIFICATION_SOURCE_MAP: Record<RectificationSource, string> = {
  [RectificationSource.QUALITY_CHECK]: '质量检查',
  [RectificationSource.OWNER_ACCEPTANCE]: '甲方验收',
  [RectificationSource.SAFETY_CHECK]: '安全检查',
  [RectificationSource.OTHER]: '其他'
};

export const RECTIFICATION_SEVERITY_MAP: Record<RectificationSeverity, { label: string; color: string; deduction: number }> = {
  [RectificationSeverity.MINOR]: { label: '一般', color: 'warning', deduction: 10 },
  [RectificationSeverity.MAJOR]: { label: '重要', color: 'orange', deduction: 30 },
  [RectificationSeverity.CRITICAL]: { label: '严重', color: 'error', deduction: 50 }
};

// ==================== 项目模板相关类型 ====================

/**
 * 模板类型枚举
 * Template Type Enum
 */
export enum TemplateType {
  FORESTRY = 'forestry',   // 林建模板
  CIVIL = 'civil',         // 土建模板
  CUSTOM = 'custom'        // 自定义模板
}

/**
 * 模板计价方式枚举
 * Template Price Type Enum
 */
export enum TemplatePriceType {
  UNIT = 'unit',     // 单价
  TOTAL = 'total'    // 总价
}

/**
 * 工程项模板项接口
 * Work Item Template Item Interface
 * 
 * 定义模板中每个工程项的结构
 */
export interface IWorkItemTemplate {
  id: string;                      // 临时ID（用于关联父子关系）
  parentId?: string;               // 父项ID（顶级项为空）
  name: string;                    // 工程项名称
  category: string;                // 工程类别（土建/园林/水电/其他）
  unit: string;                    // 计量单位
  defaultQuantity?: number;        // 默认工程量
  priceType: TemplatePriceType;    // 计价方式
  suggestedUnitPrice?: number;     // 建议单价
  sortOrder: number;               // 排序序号
  isRequired: boolean;             // 是否必选项
  description?: string;            // 说明/备注
  children?: IWorkItemTemplate[];  // 子项（树形结构时使用）
}

/**
 * 项目模板接口
 * Project Template Interface
 */
export interface IProjectTemplate {
  _id: string;                            // 模板ID
  name: string;                           // 模板名称
  type: TemplateType;                     // 模板类型
  description?: string;                   // 模板描述
  workItemTemplates: IWorkItemTemplate[]; // 工程项模板列表
  
  // 使用统计
  usageCount: number;                     // 使用次数
  lastUsedAt?: string;                    // 最后使用时间
  
  // 系统预设标记
  isSystem: boolean;                      // 是否系统预设
  
  // 审计字段
  createdBy?: string | { _id: string; username: string };
  createdAt: string;
  updatedAt: string;
}

/**
 * 创建模板请求参数
 * Create Template Request Params
 */
export interface ICreateTemplateParams {
  name: string;
  type?: TemplateType;
  description?: string;
  workItemTemplates?: IWorkItemTemplate[];
}

/**
 * 从工程项创建模板请求参数
 * Create Template From Work Items Request Params
 * 
 * 注意：使用 projectId 替代 contractId
 */
export interface ICreateTemplateFromWorkItemsParams {
  name: string;
  description?: string;
  projectId?: string;      // 项目ID（从项目获取所有工程项）
  workItemIds?: string[];  // 工程项ID列表（指定特定工程项）
}

/**
 * 应用模板请求参数
 * Apply Template Request Params
 * 
 * 注意：使用 projectId 替代 contractId
 */
export interface IApplyTemplateParams {
  projectId: string;                           // 项目ID（必填）
  selectedItems?: string[];                    // 选中的模板项ID
  quantityOverrides?: Record<string, number>;  // 数量覆盖 { templateItemId: quantity }
  priceOverrides?: Record<string, number>;     // 价格覆盖 { templateItemId: price }
}

/**
 * 应用模板响应
 * Apply Template Response
 */
export interface IApplyTemplateResponse {
  createdCount: number;
  workItems: IWorkItem[];
}

/**
 * 模板类型映射
 * Template Type Map
 */
export const TEMPLATE_TYPE_MAP: Record<TemplateType, { label: string; color: string; icon: string }> = {
  [TemplateType.FORESTRY]: { label: '林建模板', color: 'green', icon: '🌲' },
  [TemplateType.CIVIL]: { label: '土建模板', color: 'orange', icon: '🏗️' },
  [TemplateType.CUSTOM]: { label: '自定义模板', color: 'blue', icon: '📋' }
};

/**
 * 工程类别选项
 * Work Category Options
 */
export const WORK_CATEGORY_OPTIONS = [
  { value: '土建', label: '土建' },
  { value: '园林', label: '园林' },
  { value: '外墙', label: '外墙' },
  { value: '其他', label: '其他' }
];

/**
 * 计量单位选项
 * Unit Options
 */
export const UNIT_OPTIONS = [
  { value: '平方米', label: '平方米 (m²)' },
  { value: '立方米', label: '立方米 (m³)' },
  { value: '米', label: '米 (m)' },
  { value: '个', label: '个' },
  { value: '套', label: '套' },
  { value: '株', label: '株' },
  { value: '棵', label: '棵' },
  { value: '吨', label: '吨 (t)' },
  { value: '千克', label: '千克 (kg)' },
  { value: '项', label: '项' },
  { value: '次', label: '次' }
];
